'use client';

import React, { useState, useEffect } from 'react';
import { TextField, Alert, Fade, Button } from '@mui/material';
import { containsContactInfo, getBackendURL } from '../../lib/util';
import { Venue } from '../../../../shared/models';
import ErrorPopup from '../../components/ErrorPopup';
import TemplateIcon from '@mui/icons-material/Description';
import MessageTemplateSelector from './raw_space_offer/MessageTemplateSelector';
import dayjs from 'dayjs';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';

interface MessageComponentProps {
    adminInquiryId: string;
    venueId: string;
    onSuccess?: () => void;
    onMessageSent?: (message: string) => void;
    venueName?: string;
    customerName?: string;
    inquiryDateTime?: string;
    guestCount?: number;
}

export default function MessageComponent({
    adminInquiryId,
    venueId,
    onSuccess,
    onMessageSent,
    venueName = "our venue",
    customerName = "the customer",
    inquiryDateTime,
    guestCount
}: MessageComponentProps) {
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showInstructions, setShowInstructions] = useState(true);
    const [showWarning, setShowWarning] = useState(false);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [error, setError] = useState<{ isError: boolean, message: string, details?: string, error?: Error }>({
        isError: false,
        message: '',
        details: '',
        error: undefined
    });

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newMessage = e.target.value;
        setMessage(newMessage);
        setShowWarning(containsContactInfo(newMessage));
        if (newMessage) {
            setShowInstructions(false);
        } else {
            setShowInstructions(true);
        }
    };

    // Apply template variables to the template
    const applyTemplateVariables = (template: string) => {
        const today = dayjs();
        const inquiryDate = inquiryDateTime ? dayjs(inquiryDateTime) : today;

        const variables: Record<string, string | number> = {
            'venueName': venueName,
            'customerName': customerName,
            'date': today.format('MMMM D, YYYY'),
            'inquiryDate': inquiryDate.format('MMMM D, YYYY'),
            'guestCount': guestCount || 0,
            'currentDate': today.format('MMMM D, YYYY'),
            'currentTime': today.format('h:mm A')
        };

        let result = template;
        Object.entries(variables).forEach(([key, value]) => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            result = result.replace(regex, String(value));
        });

        return result;
    };

    // Handle template selection
    const handleSelectTemplate = (templateContent: string) => {
        const processedContent = applyTemplateVariables(templateContent);
        setMessage(processedContent);
        setShowWarning(containsContactInfo(processedContent));
        if (processedContent) {
            setShowInstructions(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Determine if we're in production or development
            const isProduction = window.location.hostname !== 'localhost' &&
                !window.location.hostname.includes('127.0.0.1');

            let success = false;

            if (isProduction) {
                // In production, write directly to Firestore
                const db = getFirestore();

                // Add document to customer_message_queue collection
                const queueRef = collection(db, 'customer_message_queue');
                await addDoc(queueRef, {
                    adminInquiryId: adminInquiryId,
                    venueId: venueId,
                    message: message.trim(),
                    createdAt: serverTimestamp(),
                    status: 'pending'
                });

                success = true;
            } else {
                // In development, use the API endpoint
                const response = await fetch(`${getBackendURL('sendMessageToCustomer')}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        adminInquiryId: adminInquiryId,
                        venueId: venueId,
                        message: message.trim(),
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to send message');
                }

                success = data.success;
                if (!success) {
                    throw new Error(data.error || 'Failed to send message');
                }
            }

            if (success) {
                setMessage('');
                setShowSuccess(true);
                setTimeout(() => {
                    onSuccess?.();
                }, 1500);

                onMessageSent?.(message.trim());
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setError({
                isError: true,
                message: 'Failed to send message',
                details: err instanceof Error ?
                    `${err.message}\n\n${err.stack || ''}` :
                    JSON.stringify(err, null, 2),
                error: err instanceof Error ? err : new Error(JSON.stringify(err))
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseError = () => {
        setError({
            isError: false,
            message: '',
            details: '',
            error: undefined
        });
    };

    return (
        <div className="space-y-1">


            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-white font-medium text-sm">Message</div>
                    <button
                        onClick={() => setShowTemplateSelector(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/20 rounded-lg text-xs transition-all duration-200"
                    >
                        <TemplateIcon fontSize="small" />
                        Templates
                    </button>
                </div>

                <TextField
                    multiline
                    minRows={3}
                    maxRows={8}
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Type your message here..."
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                            },
                            '& textarea': {
                                fontSize: '14px',
                                lineHeight: '1.4',
                                padding: '10px',
                                '&::placeholder': {
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    opacity: 1,
                                },
                                '&::-webkit-scrollbar': {
                                    width: '6px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '3px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(255, 255, 255, 0.3)',
                                    borderRadius: '3px',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.4)',
                                    },
                                },
                            },
                        },
                    }}
                />

                <div className="flex justify-end mt-3">
                    <button
                        onClick={handleSubmit}
                        disabled={!message.trim() || loading}
                        className={`
                            px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
                            ${!message.trim() || loading
                                ? 'bg-white/20 text-white/50 cursor-not-allowed'
                                : 'bg-white hover:bg-white/90 text-black hover:shadow-lg'
                            }
                        `}
                    >
                        {loading ? 'Sending...' : 'Send Message'}
                    </button>
                </div>
            </div>

            <ErrorPopup
                open={error.isError}
                onClose={handleCloseError}
                title="Message Error"
                message={error.message}
                details={error.details}
                error={error.error}
            />

            {/* Message Template Selector */}
            <MessageTemplateSelector
                venueId={venueId}
                onSelectTemplate={handleSelectTemplate}
                isOpen={showTemplateSelector}
                onClose={() => setShowTemplateSelector(false)}
            />
        </div>
    );
}
